import React, { useEffect, useState } from 'react';
import { TaskCommentDTO } from './TaskCommentApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faTrash, faEye } from '@fortawesome/free-solid-svg-icons';
import { TaskCommentApi } from './TaskCommentApi';
import { useNavigate } from 'react-router-dom';

type NestedCommentDTO = TaskCommentDTO & { children?: NestedCommentDTO[] };

export const TaskCommentsList: React.FC<{ taskId: string }> = ({ taskId }) => {
  const [comments, setComments] = useState<NestedCommentDTO[]>([]);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user?.tenantId || '';  
  const fetchComments = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await TaskCommentApi.filter({taskId,tenantId});
      setComments(res.data);
    } catch (err) {
      setError('Failed to load comments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [taskId]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this comment?')) {
      await TaskCommentApi.delete(id);
      fetchComments();
    }
  };

  const renderNestedComments = (
    commentList: NestedCommentDTO[],
    depth = 0
  ): JSX.Element[] => {
    return commentList.flatMap((comment) => {
      const isExpanded = expandedIds[comment.id];
      const hasChildren = comment.children && comment.children.length > 0;
      const indent = depth * 20;

      const row = (
        <tr key={comment.id} className="hover:bg-gray-100">
          <td className="p-2 border text-center">
            {hasChildren && (
              <button onClick={() => toggleExpand(comment.id)}>
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
              </button>
            )}
          </td>
          <td className="p-2 border" style={{ paddingLeft: `${indent}px` }}>
            {comment.userName || comment.employeeName}
          </td>
          <td className="p-2 border">{comment.content}</td>
          <td className="p-2 border">{comment.createdAt?.slice(0, 10)}</td>
          <td className="p-2 border space-x-2">
            <button
              onClick={() => navigate(`/task-comments/${comment.id}`)}
              className="text-blue-600 hover:underline"
              title="View"
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
            <button
              onClick={() => handleDelete(comment.id)}
              className="text-red-600 hover:underline"
              title="Delete"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </td>
        </tr>
      );

      const childrenRows =
        hasChildren && isExpanded
          ? renderNestedComments(comment.children!, depth + 1)
          : [];

      return [row, ...childrenRows];
    });
  };

  return (
    <div className="p-4 bg-white rounded shadow mt-4">
      <h2 className="text-lg font-semibold mb-4">Task Comments</h2>
      {loading ? (
        <div className="text-center">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border w-8 text-center">▶</th>
                <th className="p-2 border">User</th>
                <th className="p-2 border">Comment</th>
                <th className="p-2 border">Date</th>
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>{renderNestedComments(comments)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};